import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import {
  getPaymentAccountsAction,
  getQuickpayAction,
  getQuickpayAutopaysAction,
  getQuickpayCollateralSummaryAction,
  getQuickpayLoanSetupAction,
  getQuickpayStatusAction,
} from 'actions/quickpay';
import { checkIfApplePayIsAvailable } from 'utils/helper';
import Loader from 'components/Loader';
import {
  isCryptoLowerLtvCheckEligible,
  navigateQuickpayLanding,
  parseAutopayIntent,
  resolveQuickpayLandingTarget,
  shouldRedirectToLowerLtv,
} from './quickpayLandingRedirect';
import styles from './styles.module.scss';

const Landing = () => {
  const { uid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const quickpayData = useSelector((state) => state.quickpay.data);
  const quickpayDataRef = useRef(quickpayData);
  const [errorMsg, setErrorMsg] = useState('');

  quickpayDataRef.current = quickpayData;

  const handleError = (err) => {
    if (get(err, 'data.code') === 'TOKEN_EXPIRED') {
      navigate('/quickpay/link-expired');
    } else {
      setErrorMsg(get(err, 'data.message') || 'An error occurred');
    }
  };

  useEffect(() => {
    const refreshData = () => {
      if (location.pathname === '/quickpay/simple-pay') {
        navigate(`/quickpay/${uid}`);
      }
    };

    window.addEventListener('popstate', refreshData);

    return () => window.removeEventListener('popstate', refreshData);
  }, [location.pathname, uid, navigate]);

  useEffect(() => {
    if (!uid) return;

    const intentAutopay = parseAutopayIntent(location.search);

    const handleLandingNavigation = (accounts, autopaysPayload) => {
      const fromPathname = get(location, 'state.from.pathname');
      const isApplePayAvailable = checkIfApplePayIsAvailable();
      const { goToUrl, navigateState } = resolveQuickpayLandingTarget({
        fromPathname,
        accounts,
        isApplePayAvailable,
        intentAutopay,
        autopaysPayload,
      });
      const addPaymentWithAutopayIntent = intentAutopay && goToUrl === '/quickpay/add-payment-method';
      const navigateDefault = () => navigateQuickpayLanding({
        navigate,
        goToUrl,
        navigateState,
        addPaymentWithAutopayIntent,
      });

      const data = quickpayDataRef.current;
      const { loanId } = data || {};

      if (!isCryptoLowerLtvCheckEligible(data)) {
        navigateDefault();
        return;
      }

      dispatch(getQuickpayCollateralSummaryAction({
        loanId,
        success: (summaryData) => {
          dispatch(getQuickpayLoanSetupAction({
            loanId,
            success: (setupData) => {
              if (shouldRedirectToLowerLtv(data, summaryData, setupData)) {
                navigate('/quickpay/lower-your-ltv', { replace: true });
              } else {
                navigateDefault();
              }
            },
            fail: navigateDefault,
          }));
        },
        fail: navigateDefault,
      }));
    };

    dispatch(getQuickpayAction({
      uid,
      success: () => {
        dispatch(getPaymentAccountsAction({
          success: (accounts) => {
            dispatch(getQuickpayStatusAction({
              success: () => {
                dispatch(getQuickpayAutopaysAction({
                  success: (autopaysPayload) => handleLandingNavigation(accounts, autopaysPayload),
                  fail: handleError,
                }));
              },
              fail: handleError,
            }));
          },
          fail: handleError,
        }));
      },
      fail: handleError,
    }));
  }, []);

  return (
    <div className={styles.loader}>
      {errorMsg || <Loader className="height-2" />}
    </div>
  );
};

Landing.propTypes = {};

export default Landing;
